import React, { useState, useMemo } from "react";
import { PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon } from "../icons";
import S from "../styles";

function CellInput({ value, onChange, placeholder, type = "text", style: s }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...S.cellInput, ...s }}
    />
  );
}

function SortArrow({ sortCol, col, sortDir }) {
  if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>&#8597;</span>;
  return <span style={{ marginLeft: 4, color: "#0033FF" }}>{sortDir === "asc" ? "\u2191" : "\u2193"}</span>;
}

export default function GenericSupplyTable({ title, items, setItems, columns, onAdd, onEdit, onDelete, rowStyle, rowActions }) {
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const emptyRow = () => columns.reduce((o, c) => ({ ...o, [c.key]: "" }), {});
  const [newRow, setNewRow] = useState(emptyRow());
  const [filter, setFilter] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [err, setErr] = useState("");

  const filtered = useMemo(() => {
    let l = items;
    if (filter) {
      const f = filter.toLowerCase();
      l = l.filter((item) =>
        columns.some((c) => String(item[c.key] || "").toLowerCase().includes(f))
      );
    }
    if (sortCol) {
      l = [...l].sort((a, b) => {
        let va = a[sortCol], vb = b[sortCol];
        const col = columns.find((c) => c.key === sortCol);
        if (col && col.type === "number") {
          va = parseFloat(va) || 0;
          vb = parseFloat(vb) || 0;
          return sortDir === "asc" ? va - vb : vb - va;
        }
        va = String(va || "").toLowerCase();
        vb = String(vb || "").toLowerCase();
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return l;
  }, [items, filter, sortCol, sortDir, columns]);

  const handleSort = (c) => {
    if (sortCol === c) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(c); setSortDir("asc"); }
  };

  const saveEdit = () => {
    const required = columns.filter((c) => !c.type);
    if (required.some((c) => !editRow[c.key])) return;
    const updated = { ...editRow, ...parsePrices(editRow) };
    setItems(items.map((item) => (item.id === editId ? updated : item)));
    if (onEdit) onEdit(editId, updated);
    setEditId(null);
  };

  // The row disappears optimistically, but the server is allowed to refuse —
  // deleting hardware that products still require returns 409. If onDelete
  // rejects, put the row back and show why, rather than leaving the screen
  // showing a deletion that never happened.
  const deleteRow = (id) => {
    const snapshot = items;
    setErr("");
    setItems(items.filter((item) => item.id !== id));
    const result = onDelete && onDelete(id);
    if (result && typeof result.then === "function") {
      result.catch((e) => {
        setItems(snapshot);
        setErr((e && e.message) || "Could not delete that.");
      });
    }
  };

  const addRow = () => {
    const required = columns.filter((c) => !c.type);
    if (required.some((c) => !newRow[c.key])) return;
    const parsed = { ...newRow, ...parsePrices(newRow) };
    if (onAdd) {
      onAdd(parsed, (created) => { setItems([...items, created]); });
    } else {
      const id = Date.now();
      setItems([...items, { ...parsed, id }]);
    }
    setAddingNew(false);
    setNewRow(emptyRow());
  };

  const parsePrices = (row) => {
    const result = {};
    columns.forEach((c) => {
      if (c.isCurrency) result[c.key] = parseFloat(row[c.key]) || 0;
    });
    return result;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={`Filter ${title.toLowerCase()}...`} style={{ ...S.input, marginBottom: 0, maxWidth: 280 }} />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 15 }}>{items.length} item{items.length !== 1 ? "s" : ""}</span>
          {!addingNew && <button onClick={() => setAddingNew(true)} style={S.addBtn}><PlusIcon /> Add {title}</button>}
        </div>
      </div>
      {err && (
        <div style={{ border: "2px solid #C2410C", background: "rgba(194,65,15,0.08)", color: "#C2410C", padding: "10px 14px", marginBottom: 14, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>{err}</span>
          <button onClick={() => setErr("")} style={{ ...S.iconBtn, color: "#C2410C" }} title="Dismiss"><XIcon /></button>
        </div>
      )}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} onClick={() => handleSort(c.key)} style={{ ...S.th, width: c.width, cursor: "pointer", userSelect: "none", textAlign: c.align || "left" }}>
                  {c.label}<SortArrow sortCol={sortCol} col={c.key} sortDir={sortDir} />
                </th>
              ))}
              <th style={{ ...S.th, width: "12%", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {addingNew && (
              <tr style={S.addingRow}>
                {columns.map((c) => (
                  <td key={c.key} style={S.td}>
                    <CellInput
                      value={newRow[c.key]}
                      onChange={(v) => setNewRow({ ...newRow, [c.key]: v })}
                      placeholder={c.placeholder}
                      type={c.type || "text"}
                      style={{ textAlign: c.align || "left" }}
                    />
                  </td>
                ))}
                <td style={{ ...S.td, textAlign: "right" }}>
                  <button onClick={addRow} style={S.iconBtn} title="Save"><CheckIcon /></button>
                  <button onClick={() => { setAddingNew(false); setNewRow(emptyRow()); }} style={{ ...S.iconBtn, marginLeft: 6 }} title="Cancel"><XIcon /></button>
                </td>
              </tr>
            )}
            {filtered.map((item) => (
              <tr key={item.id} style={editId === item.id ? S.editingRow : (rowStyle ? rowStyle(item) : {})}>
                {editId === item.id ? (
                  <>
                    {columns.map((c) => (
                      <td key={c.key} style={S.td}>
                        <CellInput
                          value={editRow[c.key]}
                          onChange={(v) => setEditRow({ ...editRow, [c.key]: v })}
                          type={c.type || "text"}
                          style={{ textAlign: c.align || "left" }}
                        />
                      </td>
                    ))}
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={saveEdit} style={S.iconBtn} title="Save"><CheckIcon /></button>
                      <button onClick={() => setEditId(null)} style={{ ...S.iconBtn, marginLeft: 6 }} title="Cancel"><XIcon /></button>
                    </td>
                  </>
                ) : (
                  <>
                    {columns.map((c) => (
                      <td key={c.key} style={{
                        ...S.td,
                        fontFamily: (c.type === "number" || c.isCurrency) ? "'JetBrains Mono', monospace" : undefined,
                        fontSize: (c.type === "number" || c.isCurrency) ? 15 : undefined,
                        textAlign: c.align || "left",
                        color: c.isCurrency ? "#0033FF" : (c.key === "sku" || c.key === "diameter" || c.key === "weight" || c.key === "size") ? "rgba(10,10,15,0.58)" : "#0A0A0F",
                      }}>
                        {c.render
                          ? c.render(item)
                          : c.isCurrency
                            ? `$${(parseFloat(item[c.key]) || 0).toFixed(2)}`
                            : (item[c.key] === 0 ? 0 : item[c.key] || "—")}
                      </td>
                    ))}
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {rowActions ? rowActions(item) : null}
                      <button onClick={() => { setEditId(item.id); setEditRow({ ...item }); }} style={S.iconBtn} title="Edit"><EditIcon /></button>
                      <button onClick={() => deleteRow(item.id)} style={{ ...S.iconBtn, marginLeft: 6 }} title="Delete"><TrashIcon /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {filtered.length === 0 && !addingNew && (
              <tr>
                <td colSpan={columns.length + 1} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 40 }}>
                  {filter ? `No ${title.toLowerCase()} match.` : `No ${title.toLowerCase()} yet.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
